import { useMemo, useState } from "react";
import { EU_COUNTRIES } from "../data/vatData";
import { fmtPct } from "../lib/format";
import { useI18n } from "../i18n/I18nContext";
import { Input } from "./ui/input";
import { ArrowDown, ArrowUp, ArrowUpDown, Search } from "lucide-react";

export default function VatRatesTable() {
    const { t } = useI18n();
    const [q, setQ] = useState("");
    const [sortBy, setSortBy] = useState("name");
    const [dir, setDir] = useState("asc");

    const COLUMNS = [
        { id: "name", label: t("rates.country") },
        { id: "standard", label: t("rates.standard") },
        { id: "reduced1", label: t("rates.reduced1") },
        { id: "reduced2", label: t("rates.reduced2") },
    ];

    const rows = useMemo(() => {
        const filtered = EU_COUNTRIES.filter((c) => c.name.toLowerCase().includes(q.toLowerCase()));
        const sorted = [...filtered].sort((a, b) => {
            const av = a[sortBy];
            const bv = b[sortBy];
            if (av === null && bv === null) return 0;
            if (av === null) return 1;
            if (bv === null) return -1;
            if (typeof av === "string") return dir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
            return dir === "asc" ? av - bv : bv - av;
        });
        return sorted;
    }, [q, sortBy, dir]);

    const toggleSort = (col) => {
        if (sortBy === col) setDir((d) => (d === "asc" ? "desc" : "asc"));
        else {
            setSortBy(col);
            setDir("asc");
        }
    };

    return (
        <section id="rates" data-testid="rates-table-section" className="max-w-7xl mx-auto px-4 sm:px-6 py-16 scroll-mt-20">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
                <div>
                    <div className="text-xs font-semibold tracking-[0.2em] uppercase text-slate-500">
                        {t("rates.eyebrow")}
                    </div>
                    <h2 className="font-display mt-3 text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold text-slate-900">
                        {t("rates.title")}
                    </h2>
                </div>
                <div className="relative w-full sm:w-72">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                        data-testid="rates-search-input"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder={t("rates.search")}
                        className="pl-9 h-11 border-slate-200 focus-visible:ring-[#10B981] focus-visible:border-[#10B981]"
                    />
                </div>
            </div>

            <div className="mt-6 rounded-xl border border-slate-200 bg-white overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="rates-table">
                        <thead className="bg-slate-50 sticky top-0">
                            <tr className="text-left">
                                {COLUMNS.map((col) => (
                                    <th
                                        key={col.id}
                                        className="px-5 py-3 text-xs font-semibold tracking-[0.14em] uppercase text-slate-500"
                                    >
                                        <button
                                            data-testid={`sort-${col.id}`}
                                            onClick={() => toggleSort(col.id)}
                                            className="inline-flex items-center gap-1.5 hover:text-slate-900 transition-colors"
                                        >
                                            {col.label}
                                            {sortBy === col.id ? (
                                                dir === "asc" ? (
                                                    <ArrowUp className="h-3 w-3" />
                                                ) : (
                                                    <ArrowDown className="h-3 w-3" />
                                                )
                                            ) : (
                                                <ArrowUpDown className="h-3 w-3 opacity-40" />
                                            )}
                                        </button>
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((c) => (
                                <tr
                                    key={c.code}
                                    data-testid={`rates-row-${c.code}`}
                                    className="border-t border-slate-100 hover:bg-slate-50/70 transition-colors"
                                >
                                    <td className="px-5 py-3 font-medium text-slate-900">
                                        <span className="mr-2 text-base">{c.flag}</span>
                                        {c.name}
                                    </td>
                                    <td className="px-5 py-3 font-mono text-slate-900 font-semibold">{fmtPct(c.standard)}</td>
                                    <td className="px-5 py-3 font-mono text-slate-700">
                                        {c.reduced1 == null ? "—" : fmtPct(c.reduced1)}
                                    </td>
                                    <td className="px-5 py-3 font-mono text-slate-700">
                                        {c.reduced2 == null ? "—" : fmtPct(c.reduced2)}
                                    </td>
                                </tr>
                            ))}
                            {rows.length === 0 && (
                                <tr>
                                    <td colSpan={4} className="px-5 py-12 text-center text-slate-500">
                                        {t("rates.noMatch", { q })}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
            <p className="mt-3 text-xs text-slate-500">{t("rates.lastUpdated")}</p>
        </section>
    );
}
